<script setup>
import { computed, nextTick, onMounted, reactive, ref, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { useUISettings } from 'dashboard/composables/useUISettings';
import { useAlert } from 'dashboard/composables';

import ContactPortfolioFilters from 'dashboard/components-next/Contacts/Portfolio/ContactPortfolioFilters.vue';
import ContactPortfolioBulkActionBar from '../components/ContactPortfolioBulkActionBar.vue';
import ContactsTable from 'dashboard/components-next/Contacts/Pages/ContactsTable.vue';
import PaginationFooter from 'dashboard/components-next/pagination/PaginationFooter.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import ContactAPI from 'dashboard/api/contacts';

const DEFAULT_SORT_FIELD = 'last_activity_at';

const store = useStore();
const route = useRoute();
const router = useRouter();
const { t } = useI18n();
const { uiSettings } = useUISettings();

const contacts = useMapGetter('contacts/getContactsList');
const uiFlags = useMapGetter('contacts/getUIFlags');
const meta = useMapGetter('contacts/getMeta');

const pageNumber = computed(() => Number(route.query?.page) || 1);
const currentPage = computed(() => Number(meta.value?.currentPage));
const totalItems = computed(() => meta.value?.count);
const isFetchingList = computed(() => uiFlags.value.isFetching);
const hasContacts = computed(() => contacts.value.length > 0);

const filters = ref({
  search: route.query.search?.toString() || '',
  responsibleAgentId: route.query.responsibleAgentId?.toString() || '',
  teamId: route.query.teamId?.toString() || '',
  labelIds: route.query.labelIds
    ? String(route.query.labelIds).split(',').filter(Boolean)
    : [],
  assignmentStatus: route.query.assignmentStatus?.toString() || '',
});

const { contacts_sort_by: contactSortBy = '' } = uiSettings.value ?? {};
const parseSortSettings = (sortString = '') => {
  const hasDescending = sortString.startsWith('-');
  const sortField = hasDescending ? sortString.slice(1) : sortString;
  return {
    sort: sortField || DEFAULT_SORT_FIELD,
    order: hasDescending ? '-' : '',
  };
};

const { sort: initialSort, order: initialOrder } =
  parseSortSettings(contactSortBy);

const sortState = reactive({
  activeSort: initialSort,
  activeOrdering: initialOrder,
});

const selectedColumns = ref(['name', 'phone', 'responsibleAgent', 'company']);

const selectedContactIds = ref([]);
const selectAllResults = ref(false);
const isBulkActionLoading = ref(false);
const confirmDialogRef = ref(null);
const pendingAction = ref(null);

const visibleContactIds = computed(() => contacts.value.map(contact => contact.id));
const selectedCount = computed(() => selectedContactIds.value.length);
const hasSelection = computed(
  () => selectedCount.value > 0 || selectAllResults.value
);
const effectiveCount = computed(() =>
  selectAllResults.value ? totalItems.value : selectedCount.value
);

const buildFiltersPayload = () => ({
  search: filters.value.search || undefined,
  responsible_agent_id: filters.value.responsibleAgentId || undefined,
  team_id: filters.value.teamId || undefined,
  assignment_status: filters.value.assignmentStatus || undefined,
  label_ids: filters.value.labelIds?.length ? filters.value.labelIds : undefined,
});

const buildBulkPayload = actionPayload => {
  const payload = {
    ...actionPayload,
    select_all: selectAllResults.value,
  };

  if (selectAllResults.value) {
    payload.filters = buildFiltersPayload();
  } else {
    payload.ids = selectedContactIds.value;
  }

  return payload;
};

const clearSelection = () => {
  selectedContactIds.value = [];
  selectAllResults.value = false;
  pendingAction.value = null;
};

const toggleSelectAll = shouldSelect => {
  selectAllResults.value = false;
  selectedContactIds.value = shouldSelect ? [...visibleContactIds.value] : [];
};

const toggleContactSelection = ({ id, value }) => {
  selectAllResults.value = false;
  const isAlreadySelected = selectedContactIds.value.includes(id);
  const shouldSelect = value ?? !isAlreadySelected;

  if (shouldSelect && !isAlreadySelected) {
    selectedContactIds.value = [...selectedContactIds.value, id];
  } else if (!shouldSelect && isAlreadySelected) {
    selectedContactIds.value = selectedContactIds.value.filter(
      contactId => contactId !== id
    );
  }
};

const openConfirmDialog = async action => {
  pendingAction.value = action;
  await nextTick();
  confirmDialogRef.value?.open?.();
};

const confirmDialogTitle = computed(() => {
  const count = effectiveCount.value;
  const action = pendingAction.value?.type;
  if (!action) return '';

  const titles = {
    assign_responsible_agent:
      'CONTACT_PORTFOLIO.BULK_ACTIONS.CONFIRM.ASSIGN_RESPONSIBLE_TITLE',
    assign_department:
      'CONTACT_PORTFOLIO.BULK_ACTIONS.CONFIRM.ASSIGN_DEPARTMENT_TITLE',
    add_labels: 'CONTACT_PORTFOLIO.BULK_ACTIONS.CONFIRM.ADD_TAGS_TITLE',
    remove_labels: 'CONTACT_PORTFOLIO.BULK_ACTIONS.CONFIRM.REMOVE_TAGS_TITLE',
    remove_from_portfolio:
      'CONTACT_PORTFOLIO.BULK_ACTIONS.CONFIRM.REMOVE_FROM_PORTFOLIO_TITLE',
  };

  return t(titles[action], { count });
});

const confirmDialogDescription = computed(() => {
  const count = effectiveCount.value;
  const action = pendingAction.value;
  if (!action) return '';

  const descriptions = {
    assign_responsible_agent:
      'CONTACT_PORTFOLIO.BULK_ACTIONS.CONFIRM.ASSIGN_RESPONSIBLE_DESCRIPTION',
    assign_department:
      'CONTACT_PORTFOLIO.BULK_ACTIONS.CONFIRM.ASSIGN_DEPARTMENT_DESCRIPTION',
    add_labels: 'CONTACT_PORTFOLIO.BULK_ACTIONS.CONFIRM.ADD_TAGS_DESCRIPTION',
    remove_labels:
      'CONTACT_PORTFOLIO.BULK_ACTIONS.CONFIRM.REMOVE_TAGS_DESCRIPTION',
    remove_from_portfolio:
      'CONTACT_PORTFOLIO.BULK_ACTIONS.CONFIRM.REMOVE_FROM_PORTFOLIO_DESCRIPTION',
  };

  return t(descriptions[action.type], {
    count,
    agentName: action.agentName,
    teamName: action.teamName,
    labels: action.labels?.join(', '),
  });
});

const executeBulkAction = async () => {
  if (!pendingAction.value || isBulkActionLoading.value) return;

  isBulkActionLoading.value = true;
  try {
    await ContactAPI.portfolioBulk(buildBulkPayload(pendingAction.value.payload));
    useAlert(t('CONTACT_PORTFOLIO.BULK_ACTIONS.SUCCESS'));
    confirmDialogRef.value?.close?.();
    clearSelection();
    await fetchPortfolioContacts(pageNumber.value, { resetSelection: true });
  } catch (error) {
    useAlert(
      error?.response?.data?.error ||
        t('CONTACT_PORTFOLIO.BULK_ACTIONS.FAILED')
    );
  } finally {
    isBulkActionLoading.value = false;
  }
};

const handleAssignResponsible = ({ agentId, agentName }) => {
  openConfirmDialog({
    type: 'assign_responsible_agent',
    agentName,
    payload: {
      action_name: 'assign_responsible_agent',
      action_attributes: { responsible_agent_id: agentId },
    },
  });
};

const handleAssignDepartment = ({ teamId, teamName, agentId, agentName }) => {
  openConfirmDialog({
    type: 'assign_department',
    teamName,
    agentName,
    payload: {
      action_name: 'assign_department',
      action_attributes: {
        team_id: teamId,
        responsible_agent_id: agentId,
      },
    },
  });
};

const handleAddLabels = labels => {
  if (!labels.length) return;
  openConfirmDialog({
    type: 'add_labels',
    labels,
    payload: {
      action_name: 'add_labels',
      labels: { add: labels },
    },
  });
};

const handleRemoveLabels = labels => {
  if (!labels.length) return;
  openConfirmDialog({
    type: 'remove_labels',
    labels,
    payload: {
      action_name: 'remove_labels',
      labels: { remove: labels },
    },
  });
};

const handleRemoveFromPortfolio = () => {
  openConfirmDialog({
    type: 'remove_from_portfolio',
    payload: { action_name: 'remove_from_portfolio' },
  });
};

const buildSortAttr = () =>
  `${sortState.activeOrdering}${sortState.activeSort}`;

const buildQueryParams = (page = pageNumber.value) => {
  const query = { page: page.toString() };
  if (filters.value.search) query.search = filters.value.search;
  if (filters.value.responsibleAgentId) {
    query.responsibleAgentId = filters.value.responsibleAgentId;
  }
  if (filters.value.teamId) query.teamId = filters.value.teamId;
  if (filters.value.assignmentStatus) {
    query.assignmentStatus = filters.value.assignmentStatus;
  }
  if (filters.value.labelIds?.length) {
    query.labelIds = filters.value.labelIds.join(',');
  }
  return query;
};

const buildApiParams = (page = 1) => {
  const params = {
    page,
    sortAttr: buildSortAttr(),
  };
  if (filters.value.search) params.search = filters.value.search;
  if (filters.value.responsibleAgentId) {
    params.responsibleAgentId = filters.value.responsibleAgentId;
  }
  if (filters.value.teamId) params.teamId = filters.value.teamId;
  if (filters.value.assignmentStatus) {
    params.assignmentStatus = filters.value.assignmentStatus;
  }
  if (filters.value.labelIds?.length) {
    params.labelIds = filters.value.labelIds;
  }
  return params;
};

const fetchPortfolioContacts = async (page = 1, { resetSelection = false } = {}) => {
  if (resetSelection) {
    clearSelection();
  }
  router.replace({ query: buildQueryParams(page) });
  await store.dispatch('contacts/portfolio', buildApiParams(page));
};

const handleFilterChange = () => {
  fetchPortfolioContacts(1, { resetSelection: true });
};

const updatePage = page => {
  fetchPortfolioContacts(page);
};

watch(contacts, newContacts => {
  if (selectAllResults.value) {
    selectedContactIds.value = newContacts.map(contact => contact.id);
    return;
  }
  const idsOnPage = newContacts.map(contact => contact.id);
  selectedContactIds.value = selectedContactIds.value.filter(id =>
    idsOnPage.includes(id)
  );
});

watch(hasSelection, value => {
  if (!value) {
    confirmDialogRef.value?.close?.();
    pendingAction.value = null;
  }
});

onMounted(async () => {
  store.dispatch('agents/get');
  store.dispatch('teams/get');
  store.dispatch('labels/get');
  await fetchPortfolioContacts(pageNumber.value);
});
</script>

<template>
  <div
    class="flex flex-col flex-1 h-full m-0 overflow-auto bg-n-surface-1"
  >
    <header class="sticky top-0 z-10 px-6 py-4 border-b border-n-weak bg-n-surface-1">
      <h1 class="text-lg font-medium text-n-slate-12">
        {{ t('CONTACT_PORTFOLIO.TITLE') }}
      </h1>
      <p class="mt-1 text-sm text-n-slate-11">
        {{ t('CONTACT_PORTFOLIO.DESCRIPTION') }}
      </p>
    </header>

    <main class="flex flex-col flex-1 gap-4 px-6 py-4">
      <ContactPortfolioFilters
        v-model="filters"
        @change="handleFilterChange"
      />

      <div
        v-if="isFetchingList"
        class="flex items-center justify-center py-10 text-n-slate-11"
      >
        <Spinner />
      </div>

      <template v-else>
        <ContactPortfolioBulkActionBar
          v-if="hasSelection"
          :visible-contact-ids="visibleContactIds"
          :selected-contact-ids="selectedContactIds"
          :total-items="totalItems"
          :select-all-results="selectAllResults"
          :is-loading="isBulkActionLoading"
          @toggle-all="toggleSelectAll"
          @clear-selection="clearSelection"
          @select-all-results="selectAllResults = $event"
          @assign-responsible="handleAssignResponsible"
          @assign-department="handleAssignDepartment"
          @add-labels="handleAddLabels"
          @remove-labels="handleRemoveLabels"
          @remove-from-portfolio="handleRemoveFromPortfolio"
        />

        <div
          v-if="!hasContacts"
          class="flex items-center justify-center py-10"
        >
          <span class="text-base text-n-slate-11">
            {{ t('CONTACT_PORTFOLIO.EMPTY_STATE') }}
          </span>
        </div>

        <div v-else class="flex flex-col gap-4">
          <ContactsTable
            :contacts="contacts"
            :selected-contact-ids="selectedContactIds"
            :visible-columns="selectedColumns"
            @toggle-contact="toggleContactSelection"
          />
          <Dialog
            v-if="hasSelection"
            ref="confirmDialogRef"
            type="alert"
            :title="confirmDialogTitle"
            :description="confirmDialogDescription"
            :confirm-button-label="t('CONTACT_PORTFOLIO.BULK_ACTIONS.CONFIRM.CONFIRM')"
            :is-loading="isBulkActionLoading"
            @confirm="executeBulkAction"
          />
        </div>
      </template>
    </main>

    <footer
      v-if="!isFetchingList && hasContacts"
      class="sticky bottom-0 z-0 px-6 pb-4"
    >
      <PaginationFooter
        current-page-info="CONTACTS_LAYOUT.PAGINATION_FOOTER.SHOWING"
        :current-page="currentPage"
        :total-items="totalItems"
        :items-per-page="15"
        @update:current-page="updatePage"
      />
    </footer>
  </div>
</template>
